/**
 * Admin cockpit panel: founding-member demand (waitlist) while billing is not
 * live, plus the live/early switch state. Owner/admin only (the query enforces
 * the role server side). The admin cockpit is German-only, like the rest of
 * `AdminPage`.
 */
import { useQuery } from 'convex/react'
import { Sparkles } from 'lucide-react'
import { api } from '../../convex/_generated/api'
import { formatDate } from '../lib/i18n'
import { useBillingStatus } from '../lib/use-billing'

function Breakdown({
  title,
  counts,
}: {
  title: string
  counts: Partial<Record<string, number>>
}) {
  const entries = Object.entries(counts)
    .map(([key, count]): [string, number] => [key, count ?? 0])
    .sort((a, b) => b[1] - a[1])
  return (
    <div className="admin-breakdown">
      <strong>{title}</strong>
      {entries.length === 0 ? (
        <span>-</span>
      ) : (
        entries.map(([key, count]) => (
          <span key={key}>
            {key} <b>{count}</b>
          </span>
        ))
      )}
    </div>
  )
}

export function AdminBillingPanel() {
  const live = useBillingStatus()
  const overview = useQuery(api.waitlist.adminOverview, {})

  return (
    <article className="admin-panel admin-billing-panel">
      <Sparkles size={22} aria-hidden="true" />
      <h2>Founding Members</h2>
      <p className="admin-billing-mode">
        Billing:{' '}
        <strong>
          {live === null
            ? 'wird geladen'
            : live
              ? 'LIVE (Paywall + Checkout aktiv)'
              : 'Early Access (Warteliste statt Checkout)'}
        </strong>
      </p>
      {overview === undefined ? (
        <p>Nachfrage wird geladen...</p>
      ) : (
        <>
          <div className="metric-grid">
            <span>
              Total <strong>{overview.total}</strong>
            </span>
            <span>
              Letzte 7 Tage <strong>{overview.lastSevenDays}</strong>
            </span>
            <span>
              Pro <strong>{overview.byPlan.pro ?? 0}</strong>
            </span>
            <span>
              Team <strong>{overview.byPlan.team ?? 0}</strong>
            </span>
          </div>
          <div className="admin-breakdowns">
            <Breakdown title="Intervall" counts={overview.byInterval} />
            <Breakdown title="Sprache" counts={overview.byLocale} />
            <Breakdown title="Quelle" counts={overview.bySource} />
          </div>
          {overview.recent.length > 0 ? (
            <ul className="admin-list">
              {overview.recent.map((row) => (
                <li key={row.id}>
                  <span>{row.email}</span>
                  <small>
                    {row.plan} / {row.interval} / {row.locale} / {row.source} /{' '}
                    {formatDate(row.createdAt, 'de')}
                  </small>
                </li>
              ))}
            </ul>
          ) : (
            <p>Noch keine Founding Members.</p>
          )}
          {overview.truncated ? (
            <small>Zeigt die neuesten 5000 Einträge.</small>
          ) : null}
        </>
      )}
    </article>
  )
}
